<template>
  <div
    class="quick-search-root"
    :class="{ 'is-peeking': triggerVisible }"
    @mouseenter="onTriggerEnter"
    @mouseleave="onTriggerLeave"
  >
    <button
      class="quick-search-trigger"
      type="button"
      aria-label="Quick search"
      @focus="onTriggerFocus"
      @blur="onTriggerBlur"
      @click="openSearch"
    >
      <feather-icon icon="SearchIcon" size="18" />
      <span class="quick-search-trigger-label">Search</span>
      <kbd class="quick-search-shortcut">Ctrl K</kbd>
    </button>

    <transition name="quick-search-fade">
      <div v-if="open" class="quick-search-overlay" @click.self="closeSearch">
        <div
          class="quick-search-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Quick search"
        >
          <div class="quick-search-input-wrap">
            <span class="quick-search-input-badge">
              <feather-icon icon="SearchIcon" size="18" />
            </span>
            <input
              ref="searchInput"
              v-model="query"
              class="quick-search-input"
              type="text"
              autocomplete="off"
              spellcheck="false"
              placeholder="Search pages, modules and settings…"
              @keydown.down.prevent="moveSelection(1)"
              @keydown.up.prevent="moveSelection(-1)"
              @keydown.enter.prevent="openSelected"
              @keydown.esc.prevent="closeSearch"
            />
            <kbd class="quick-search-esc">Esc</kbd>
          </div>

          <div class="quick-search-meta">
            <span class="quick-search-meta-title">Quick jump</span>
            <span class="quick-search-meta-count">
              {{ results.length }} result{{ results.length === 1 ? "" : "s" }}
            </span>
          </div>

          <div ref="resultsList" class="quick-search-results">
            <button
              v-for="(item, index) in results"
              :key="item.route"
              type="button"
              class="quick-search-item"
              :class="{ 'is-active': index === selectedIndex }"
              @mouseenter="hoverResult(item, index)"
              @focus="hoverResult(item, index)"
              @click="navigateTo(item)"
            >
              <span class="quick-search-item-icon">
                <feather-icon :icon="item.icon" size="16" />
              </span>
              <span class="quick-search-item-title">{{ item.title }}</span>
              <span class="quick-search-item-group">{{ item.group }}</span>
              <feather-icon
                v-if="index === selectedIndex"
                icon="ArrowRightIcon"
                size="14"
                class="quick-search-item-enter"
              />
            </button>

            <div v-if="!results.length" class="quick-search-empty">
              <feather-icon icon="SearchIcon" size="26" />
              <span>{{ $t("No pages found") }}</span>
            </div>
          </div>

          <div class="quick-search-footer">
            <span><kbd>↑</kbd><kbd>↓</kbd> {{ $t("Navigate") }}</span>
            <span><kbd>Enter</kbd> {{ $t("Open") }}</span>
            <span><kbd>Esc</kbd> {{ $t("Close") }}</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { prefetchRoute } from "@/utils/routePrefetch";

export default {
  name: "QuickSearch",
  data() {
    return {
      open: false,
      query: "",
      selectedIndex: 0,
      peekVisible: false,
      triggerHovered: false,
      triggerFocused: false,
      peekHideTimer: null,
      peekShowTimer: null,
      prefetchTimer: null,
    };
  },
  computed: {
    menuGroups() {
      const menu = this.$store.state.menu;
      return Array.isArray(menu) ? menu : [];
    },
    hasMenu() {
      return this.menuGroups.some(
        (group) => group && Array.isArray(group.children) && group.children.length
      );
    },
    triggerVisible() {
      return (
        this.peekVisible ||
        this.triggerHovered ||
        this.triggerFocused ||
        this.open
      );
    },
    visibleOnThisRoute() {
      const route = this.$route;
      if (!route || !this.hasMenu) return false;

      const name = String(route.name || "").toLowerCase();
      if (/^(auth-|misc-|error-)/.test(name)) return false;
      if (route.meta && route.meta.layout === "full") return false;

      const id = route.params && route.params.id;
      if (id !== undefined && id !== null && id !== "") return false;

      const formName =
        /(^|-)(add|edit|create|update|form|details|entry|registration|profile)(-|$)/;
      if (formName.test(name)) return false;

      const path = String(route.path || "").toLowerCase();
      if (/(^|\/)(add|new|edit|create|update)(\/|$)/.test(path)) return false;
      if (/(form|details|registration|profile|entry)(\/|$)/.test(path)) {
        return false;
      }

      return true;
    },
    rights() {
      return this.$store.state.rights || {};
    },
    // Build this once per menu/rights change. Typing then only filters a small
    // cached array instead of re-walking the whole menu on every keystroke.
    searchIndex() {
      const seen = new Set();
      const items = [];

      this.menuGroups.forEach((group, groupIndex) => {
        if (!group) return;
        const groupTitle = this.$t(group.name || "");
        const children = Array.isArray(group.children) ? group.children : [];

        children.forEach((child, orderIndex) => {
          if (!child || !child.route || child.disabled) return;
          if (seen.has(child.route)) return;

          // Respect the permissions the backend sent with the menu. A child is
          // skipped only when it is explicitly view/add/edit/delete denied.
          const right = this.rights[child.route] || this.rights[child.title];
          if (
            right &&
            right.view === false &&
            right.add !== true &&
            right.edit !== true &&
            right.delete !== true
          ) {
            return;
          }

          const title = this.$t(child.title || child.name || "");
          if (!title) return;

          seen.add(child.route);
          items.push({
            route: child.route,
            title,
            group: groupTitle,
            icon: this.resolveIcon(child, group, title),
            parent: group,
            searchText: `${title} ${groupTitle}`.toLowerCase(),
            titleLower: title.toLowerCase(),
            groupIndex,
            orderIndex,
          });
        });
      });

      return items;
    },
    results() {
      const query = this.query.trim().toLowerCase();
      const score = (item) => {
        if (!query) return 1;
        if (item.titleLower === query) return 0;
        if (item.titleLower.startsWith(query)) return 1;
        if (item.titleLower.includes(query)) return 2;
        return 3;
      };

      return this.searchIndex
        .filter((item) => !query || item.searchText.includes(query))
        .sort(
          (a, b) =>
            // Relevance first while typing, but keep the sidebar's own order
            // within a score so results read "menu wise", never alphabetically.
            score(a) - score(b) ||
            a.groupIndex - b.groupIndex ||
            a.orderIndex - b.orderIndex
        )
        .slice(0, 24);
    },
  },
  watch: {
    results() {
      this.selectedIndex = 0;
      if (this.prefetchTimer) clearTimeout(this.prefetchTimer);
      if (!this.query.trim()) return;
      // Warm the top result while the user is still reading the list.
      this.prefetchTimer = setTimeout(() => {
        this.prefetchResult(this.results[0]);
      }, 180);
    },
    "$route.path"() {
      if (this.open) this.closeSearch();
    },
    open(value) {
      if (value) {
        this.clearPeekTimers();
        this.peekVisible = true;
      } else {
        this.restartPeekCycle();
      }
    },
    visibleOnThisRoute(value) {
      if (value) this.restartPeekCycle();
      else {
        this.clearPeekTimers();
        this.peekVisible = false;
      }
    },
  },
  mounted() {
    // Capture phase: some pages stop keydown propagation on their own inputs.
    window.addEventListener("keydown", this.onGlobalKeydown, true);
    this.restartPeekCycle();

    // Build the searchable index while the browser is idle so the first
    // Ctrl+K / click opens instantly instead of doing the scan on open.
    const warmSearchIndex = () => {
      // eslint-disable-next-line no-unused-expressions
      this.searchIndex;
    };
    if (window.requestIdleCallback) {
      window.requestIdleCallback(warmSearchIndex, { timeout: 2000 });
    } else {
      setTimeout(warmSearchIndex, 1200);
    }
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onGlobalKeydown, true);
    this.clearPeekTimers();
    if (this.prefetchTimer) clearTimeout(this.prefetchTimer);
  },
  methods: {
    resolveIcon(child, group, title) {
      // Menu icons can come from older/custom data and may not exist in the
      // Feather icon set (that rendered as empty boxes). Always resolve our
      // own known-good icon from the page title/route instead.
      const key = `${title} ${child.route || ""}`.toLowerCase();
      const rules = [
        [/(online|video|virtual)/, "VideoIcon"],
        [/(academic document|certificate|document|file)/, "FileTextIcon"],
        [/(audit|activity log|log)/, "ActivityIcon"],
        [/(accountability)/, "ShieldIcon"],
        [/(assessment|exam|result|marks|grade)/, "AwardIcon"],
        [/(salary|payroll|wage)/, "BriefcaseIcon"],
        [/(bank|voucher|payment|fee|collection|due|amount)/, "CreditCardIcon"],
        [/(branding|logo|theme)/, "ImageIcon"],
        [/(department)/, "BriefcaseIcon"],
        [/(timing|attendance|late)/, "ClockIcon"],
        [/(transfer)/, "RepeatIcon"],
        [/(report|analytic|chart|statistic)/, "BarChart2Icon"],
        [/(setting|config|preference)/, "SettingsIcon"],
        [/(message|chat|whatsapp|sms)/, "MessageSquareIcon"],
        [/(notification|alert)/, "BellIcon"],
        [/(calendar|planner|event)/, "CalendarIcon"],
        [/(transport|bus|vehicle)/, "TruckIcon"],
        [/(library|book)/, "BookIcon"],
        [/(inventory|stock|item)/, "PackageIcon"],
        [/(inquiry|admission|application)/, "InboxIcon"],
        [/(class|section|subject)/, "BookOpenIcon"],
        [/(dashboard|home)/, "HomeIcon"],
        [/(staff|teacher|employee)/, "UsersIcon"],
        [/(student|child)/, "UserIcon"],
        [/(campus|branch|building)/, "MapPinIcon"],
        [/(profile|account|user)/, "UserIcon"],
      ];

      const match = rules.find(([pattern]) => pattern.test(key));
      return match ? match[1] : "GridIcon";
    },
    clearPeekTimers() {
      if (this.peekHideTimer) {
        clearTimeout(this.peekHideTimer);
        this.peekHideTimer = null;
      }
      if (this.peekShowTimer) {
        clearTimeout(this.peekShowTimer);
        this.peekShowTimer = null;
      }
    },
    restartPeekCycle() {
      this.clearPeekTimers();
      if (!this.visibleOnThisRoute || this.open) {
        this.peekVisible = false;
        return;
      }

      // Peek in for a few seconds, then tuck away and come back later.
      this.peekVisible = true;
      this.peekHideTimer = setTimeout(() => {
        if (this.open || this.triggerHovered || this.triggerFocused) return;
        this.peekVisible = false;
        this.peekShowTimer = setTimeout(() => this.restartPeekCycle(), 16000);
      }, 5500);
    },
    onTriggerEnter() {
      this.triggerHovered = true;
      this.clearPeekTimers();
      this.peekVisible = true;
    },
    onTriggerLeave() {
      this.triggerHovered = false;
      if (this.open) return;
      this.clearPeekTimers();
      this.peekHideTimer = setTimeout(() => {
        if (!this.triggerFocused) this.peekVisible = false;
        this.peekShowTimer = setTimeout(() => this.restartPeekCycle(), 16000);
      }, 1400);
    },
    onTriggerFocus() {
      this.triggerFocused = true;
      this.peekVisible = true;
    },
    onTriggerBlur() {
      this.triggerFocused = false;
    },
    onGlobalKeydown(event) {
      const key = event.key ? event.key.toLowerCase() : "";
      const isK = key === "k" || event.keyCode === 75;
      const isShortcut =
        ((event.ctrlKey || event.metaKey) && (isK || key === "/")) ||
        (event.altKey && isK);

      if (isShortcut) {
        event.preventDefault();
        if (this.open) this.closeSearch();
        else this.openSearch();
        return;
      }

      if (event.key === "Escape" && this.open) {
        event.preventDefault();
        this.closeSearch();
      }
    },
    openSearch() {
      if (!this.visibleOnThisRoute) return;
      this.open = true;
      this.query = "";
      this.selectedIndex = 0;
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus();
      });
    },
    closeSearch() {
      this.open = false;
      this.query = "";
      this.selectedIndex = 0;
      if (this.prefetchTimer) clearTimeout(this.prefetchTimer);
    },
    prefetchResult(item) {
      if (!item || !item.route) return;
      prefetchRoute(this.$router, item.route);
    },
    hoverResult(item, index) {
      this.selectedIndex = index;
      this.prefetchResult(item);
    },
    moveSelection(step) {
      if (!this.results.length) return;
      const total = this.results.length;
      this.selectedIndex = (this.selectedIndex + step + total) % total;
      this.prefetchResult(this.results[this.selectedIndex]);
      this.$nextTick(() => {
        const list = this.$refs.resultsList;
        const active = list && list.children[this.selectedIndex];
        if (active && active.scrollIntoView) {
          active.scrollIntoView({ block: "nearest" });
        }
      });
    },
    openSelected() {
      const item = this.results[this.selectedIndex];
      if (item) this.navigateTo(item);
    },
    navigateTo(item) {
      if (!item || !item.route) return;
      if (item.parent) {
        this.$store.commit("setCurrentMenu", item.parent);
      }

      // Close the modal immediately so the destination page is visible as
      // soon as it resolves. No artificial "Opening ..." overlay.
      this.closeSearch();

      this.$router.push({ name: item.route }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.quick-search-root {
  position: fixed;
  right: 20px;
  bottom: 24px;
  z-index: 20000;
}

/* Animate only the trigger. The modal is a fixed child above, so the root
   must NOT have a transform (a transformed ancestor traps position: fixed). */
.quick-search-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 12px;
  border: 1px solid rgba(115, 103, 240, 0.22);
  border-radius: 999px;
  background: #fff;
  color: #5e5873;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 6px 22px rgba(30, 26, 60, 0.14);
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transform: translateX(calc(100% + 32px));
  transition: transform 0.38s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.22s ease, box-shadow 0.18s ease;
}

.quick-search-root.is-peeking .quick-search-trigger {
  opacity: 1;
  pointer-events: auto;
  transform: translateX(0);
}

.quick-search-root.is-peeking .quick-search-trigger:hover,
.quick-search-root.is-peeking .quick-search-trigger:focus {
  transform: translateY(-1px);
  box-shadow: 0 9px 26px rgba(30, 26, 60, 0.2);
  outline: none;
}

.quick-search-shortcut {
  padding: 2px 6px;
  border-radius: 5px;
  background: #f3f2f7;
  color: #8b87a1;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
}

.quick-search-overlay {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 11vh 16px 24px;
  background: rgba(20, 18, 30, 0.42);
  overscroll-behavior: contain;
}

.quick-search-panel {
  position: relative;
  width: min(720px, 100%);
  max-height: 68vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #eceaf2;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(18, 16, 32, 0.22),
    0 2px 8px rgba(18, 16, 32, 0.06);
}

.quick-search-input-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px 12px;
}

.quick-search-input-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #f0eefe;
  color: #7367f0;
  flex-shrink: 0;
}

.quick-search-input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: #3b3554;
  font-size: 16px;
  font-weight: 500;
}

.quick-search-input::placeholder {
  color: #aaa5bb;
  font-weight: 400;
}

.quick-search-esc {
  padding: 3px 7px;
  border: 1px solid #eceaf2;
  border-radius: 6px;
  background: #f8f7fc;
  color: #8b87a1;
  font-size: 10px;
  font-weight: 600;
}

.quick-search-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 22px 10px;
  color: #a29db5;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.quick-search-meta-count {
  color: #b7b2c8;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.quick-search-results {
  overflow-y: auto;
  padding: 4px 10px 10px;
  scrollbar-width: thin;
  scrollbar-color: rgba(115, 103, 240, 0.35) transparent;
}

.quick-search-results::-webkit-scrollbar {
  width: 6px;
}

.quick-search-results::-webkit-scrollbar-track {
  background: transparent;
}

.quick-search-results::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(115, 103, 240, 0.3);
}

.quick-search-item {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #3b3554;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s ease, box-shadow 0.12s ease;
}

.quick-search-item.is-active {
  background: #f1effc;
  box-shadow: inset 3px 0 0 #7367f0;
}

.quick-search-item.is-active .quick-search-item-title {
  color: #1f1a33;
}

.quick-search-item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(115, 103, 240, 0.1);
  color: #7367f0;
  flex-shrink: 0;
  transition: background 0.14s ease, color 0.14s ease;
}

/* Hover/active: tint the tile instead of inverting it to solid purple.
   The solid fill hid the icon (white glyph on white row) for a frame, which
   is what made it look like the icon flickered. */
.quick-search-item.is-active .quick-search-item-icon {
  background: rgba(115, 103, 240, 0.18);
  color: #5b4cdb;
}

.quick-search-item-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
}

.quick-search-item-group {
  overflow: hidden;
  max-width: 45%;
  padding: 3px 7px;
  border-radius: 5px;
  background: #f6f5fa;
  color: #8b87a1;
  font-size: 9px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.quick-search-item-enter {
  color: #7367f0;
  flex-shrink: 0;
  margin-left: 2px;
}

.quick-search-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 36px 16px;
  color: #9a95ad;
  font-size: 13px;
  text-align: center;
}

.quick-search-empty .feather-icon {
  color: #c3bfd4;
}

.quick-search-footer {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 10px 20px;
  border-top: 1px solid #efedf5;
  background: #fbfaff;
  color: #9a95ad;
  font-size: 11px;
}

.quick-search-footer kbd {
  padding: 1px 5px;
  margin-right: 4px;
  border: 1px solid #e9e6f2;
  border-radius: 5px;
  background: #fff;
  color: #8b87a1;
  font-size: 10px;
}

.quick-search-fade-enter-active,
.quick-search-fade-leave-active {
  transition: opacity 0.18s ease;
}

.quick-search-fade-enter-active .quick-search-panel,
.quick-search-fade-leave-active .quick-search-panel {
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.18s ease;
  will-change: transform, opacity;
}

.quick-search-fade-enter,
.quick-search-fade-leave-to {
  opacity: 0;
}

.quick-search-fade-enter .quick-search-panel,
.quick-search-fade-leave-to .quick-search-panel {
  opacity: 0;
  transform: translateY(-8px) scale(0.985);
}

@media (max-width: 768px) {
  .quick-search-root {
    right: 14px;
    bottom: 16px;
  }

  .quick-search-trigger-label,
  .quick-search-shortcut {
    display: none;
  }

  .quick-search-overlay {
    padding: 8vh 10px 16px;
  }

  .quick-search-item-group {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .quick-search-root,
  .quick-search-fade-enter-active,
  .quick-search-fade-leave-active,
  .quick-search-fade-enter-active .quick-search-panel,
  .quick-search-fade-leave-active .quick-search-panel {
    transition: none;
  }
}
</style>

